import React, { useState, useMemo, useCallback } from 'react';
import { Box, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import Loader from '../../components/Loader/Loader';
import electivesAssignmentApi from '../../apis/electivesAssignment';
import { buildElectiveColumns } from './utils/columns';
import { UpdateAssignmentRecord } from './types/ElectiveAssignmentTypes';
import {
  buildStudentsMapping,
  getAllElectives,
  isSubjectAssigned,
  updateStudentRecord
} from './utils/utils';
import { updateAssignment } from './utils/updateAssignment';
import SearchFilters from './components/SearchFilters';
import { StyledDataGrid } from './styled';
import { ElectiveSubjectsMapping } from '../../types/electiveAssignmentType';
import fetchTermIdsForClass from './utils/terms';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { PageWrapper, Title } from '../common/styled';

const StudentsElectiveSubjectsPage: React.FC = () => {
  const school = useSelector((state: RootState) => state.profile.user.school);
  const user = useSelector((state: RootState) => state.profile.user);

  const [academicYear, setAcademicYear] = useState('');
  const [students, setStudents] = useState<ElectiveSubjectsMapping[]>([]);
  const [termIds, setTermIds] = useState<string[]>([]);
  const [originalStudents, setOriginalStudents] = useState<
    ElectiveSubjectsMapping[]
  >([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignmentChanges, setAssignmentChanges] =
    useState<UpdateAssignmentRecord>({});
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: SnackbarSeverityType.success
  });
  const [savedGrade, setSavedGrade] = useState('');
  const [savedDivision, setSavedDivision] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const CONFIRM_MESSAGE = `Un-assigning subjects (if any) will delete the marks entered for them and the subject won't appear on the report card. If you want the subject to show, don't unassign it. Are you sure you want to save the changes?`;

  const showToast = (message: string, severity: SnackbarSeverityType) =>
    setToast({ open: true, message, severity });

  const originalStudentsById = Object.fromEntries(
    originalStudents.map((student) => [student.studentId, student])
  );

  const allElectives = getAllElectives(students);

  const unsavedChangesMap = useMemo(() => {
    const map: Record<string, Record<string, boolean>> = {};
    Object.entries(assignmentChanges).forEach(([studentId, subjects]) => {
      map[studentId] = {};
      Object.entries(subjects).forEach(([subjectKey, meta]) => {
        const subjectIdOrKey = meta.subjectId || subjectKey;
        const changed = meta.current !== meta.original;
        map[studentId][subjectIdOrKey] = changed;
        map[studentId][subjectKey] = changed;
      });
    });
    return map;
  }, [assignmentChanges]);

  const rows = students.map((student: any, id) => ({
    id: student.studentId || id,
    studentId: student.studentId,
    studentName: student.studentName,
    enrollmentNo: student.enrollmentNo,
    rollNumber: student.rollNumber || student.rollNo,
    ...Object.fromEntries(
      allElectives.map((key) => [key, isSubjectAssigned(student, key)])
    )
  }));

  const toggleAssignment = useCallback(
    (studentId: string, subjectKey: string) => {
      if (saving) return;
      setStudents((prev) =>
        prev.map((student) => {
          if (student.studentId !== studentId) return student;

          const newElectiveAssigned = !isSubjectAssigned(student, subjectKey);
          const updated = updateStudentRecord(
            student,
            subjectKey,
            newElectiveAssigned
          );
          setAssignmentChanges((prevChanges: UpdateAssignmentRecord) =>
            updateAssignment(
              prevChanges,
              studentId,
              subjectKey,
              newElectiveAssigned,
              originalStudentsById[studentId]
            )
          );
          return updated;
        })
      );
    },
    [saving, originalStudentsById]
  );

  const columns = useMemo(
    () =>
      buildElectiveColumns(
        allElectives,
        toggleAssignment,
        (row, key) => !!row[key],
        saving,
        unsavedChangesMap
      ),
    [allElectives, toggleAssignment, saving, unsavedChangesMap]
  );

  const executeSave = async () => {
    if (!school) return;
    const studentsMapping = buildStudentsMapping(
      assignmentChanges,
      students,
      originalStudents
    );
    if (!studentsMapping.length) return;

    try {
      setSaving(true);
      let effectiveTermIds: string[] = termIds;
      if (academicYear && savedGrade && savedDivision) {
        effectiveTermIds = await fetchTermIdsForClass(
          school,
          academicYear,
          savedGrade,
          savedDivision,
          showToast
        );
      }

      await electivesAssignmentApi.updateStudentElectiveSubjectsMapping(
        school,
        academicYear,
        { termIds: effectiveTermIds, studentsMapping }
      );

      setOriginalStudents(students.map((student) => ({ ...student })));
      setAssignmentChanges({});
      showToast('Elective assignments updated.', SnackbarSeverityType.success);
    } catch {
      showToast('Failed to update assignments.', SnackbarSeverityType.error);
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const handleSave = () => {
    if (!Object.keys(assignmentChanges).length) return;
    setConfirmOpen(true);
  };

  const handleResults = (
    fetched: ElectiveSubjectsMapping[],
    meta: {
      academicYear: string;
      grade: string;
      division: string;
      termIds?: string[];
    }
  ) => {
    setAcademicYear(meta.academicYear);
    setStudents(fetched);
    setOriginalStudents(fetched.map((student) => ({ ...student })));
    setAssignmentChanges({});
    setHasSearched(true);
    setTermIds(meta.termIds || []);
    setSavedGrade(meta.grade);
    setSavedDivision(meta.division);
  };

  const handleFiltersChange = () => {
    setHasSearched(false);
    setAssignmentChanges({});
  };

  return (
    <>
      <Title>Students Elective Subjects</Title>
      <PageWrapper>
        <SearchFilters
          school={school}
          user={user}
          onResults={handleResults}
          showToast={showToast}
          onLoadingChange={setLoading}
          onFiltersChange={handleFiltersChange}
        />
      </PageWrapper>
      <Loader show={loading && students.length === 0} />
      {hasSearched && (
        <PageWrapper>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            loading={loading}
          />
          {Object.keys(assignmentChanges).length > 0 && (
            <Box mt={2} display='flex' justifyContent='flex-end'>
              <Button
                variant='contained'
                onClick={handleSave}
                disabled={saving}
              >
                Update
              </Button>
            </Box>
          )}
        </PageWrapper>
      )}
      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={executeSave}
        message={CONFIRM_MESSAGE}
        disable={saving}
      />
      <ToastMessage
        {...toast}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </>
  );
};

export default StudentsElectiveSubjectsPage;
